<script type="ts">
	import { onMount } from 'svelte';
	import Input from '$Chat/Input.svelte';
	import Profiles from '$Chat/Profiles.svelte';
	import Channels from '$Chat/Channels.svelte';

	import type { ChatComponent } from './helpers/Achord';
	import type { AchordData } from 'src/app';

	export let channel: string;
	export let size: number = 5;
	export let entries: ChatComponent[] = [];
	export let online: AchordData.Profile[] = [];
	export let channels: string[] = [];
	export let scrollToText: string = '';
	/*
	* Available Events
		on:insert
		on:remove
		on:submit
		on:plus
	*/
	const height = `max( ${200 + size * 78}px, ${12 + size * 9}vh )`;

	let elContent: HTMLElement | null;
	let elMessages: HTMLElement | null;

	onMount(() => {
		if (!scrollToText || !elContent || !elMessages) {
			return;
		}

		const target = Array.from(elMessages.childNodes)
			.filter((e) => e.nodeType === Node.ELEMENT_NODE)
			.find((e) => e.textContent?.includes(scrollToText)) as HTMLElement;

		// // scroll messages dom element to target element
		if (!target) {
			return;
		}
		const relativePosition = target.offsetTop - elContent.offsetTop;

		elContent.scrollTo({
			top: relativePosition,
			behavior: 'smooth'
		});
	});
</script>

<div class="achord shadow-xl" style="--height: {height};">
	<div class="achord-sidebar">
		<div class="label">Channels</div>
		<Channels {channels} bind:active={channel} />
		<br />
		<div class="label">Online</div>
		<Profiles {online} />
	</div>

	<div class="achord-channel">
		<div class="pwn">#</div>
		{channel}
	</div>

	<div class="achord-content" bind:this={elContent}>
		<div class="messages" bind:this={elMessages}>
			{#if entries.length > 0}
				{#each entries as entry}
					<svelte:component this={entry.component} {...entry.props} on:insert on:destroy />
				{/each}
			{/if}
		</div>
	</div>
	<div class="achord-input">
		<Input {channel} on:insert on:plus />
	</div>
	<slot />
</div>

<style lang="scss">
	%scrollable {
		overflow-y: auto;
		overflow-x: hidden;
		height: 100%;

		// Nice scrollbars in Firefox
		scrollbar-color: hsl(0 0% 55% / 0.4) transparent;

		// No scrollbars in Chrome
		&::-webkit-scrollbar {
			display: none;
		}
	}

	.achord {
		background-color: var(--demo-bg-30);
		color: var(--demo-text);
		font-family: var(--font-system);
		font-size: 0.77rem;
		letter-spacing: 0.1px;
		border-radius: $border_radius;

		display: grid;
		grid-template-columns: 220px 4fr;
		grid-template-rows: 52px auto 90px;

		margin-top: 40px;
		margin-bottom: 40px;

		grid-column: ultrawide;
		height: var(--height);

		// Needed during development so that
		// that the debug-label appears
		// in the right place
		position: relative;
	}

	.achord-sidebar {
		@extend %scrollable;

		background-color: var(--demo-bg-20);
		grid-column: 1;
		grid-row: 1 / span 3;
		padding: 48px 7px 48px;
		border-bottom-left-radius: $border_radius;
		border-top-left-radius: $border_radius;

		.label {
			text-transform: uppercase;
			font-weight: 600;
			letter-spacing: 0.1px;
			color: var(--demo-text-low);
			padding: 0 14px;
			margin-bottom: 7px;
			font-size: 0.85em;
		}
	}

	.achord-input {
		grid-column: 2;
		grid-row: 3;
		padding: 11px;
	}

	.achord-channel {
		display: flex;
		align-items: center;
		padding: 0px 14px;
		font-weight: 600;
		border-bottom: 2px solid var(--demo-bg-40);

		.pwn {
			font-size: 1.5em;
			margin-right: 7px;
			color: var(--demo-bg-60);
			font-weight: 300;
		}
	}

	.achord-content {
		background-color: var(--demo-bgi);
		margin: 0;

		@extend %scrollable;
		// Auto-scroll to bottom
		flex-direction: column-reverse;
		justify-content: flex-start;
		display: flex;
		// Auto-scroll to bottom / end
	}

	.achord-input {
		padding: 18px;
	}

	.instructions {
		font-weight: 600;
		text-align: center;
		color: var(--text-color-invert);

		small {
			font-weight: 400;
		}
	}

	@media screen and (max-width: 660px) {
		.achord {
			margin-left: auto;
			margin-right: auto;
			grid-template-columns: 1fr;
			grid-template-rows: 52px auto 90px;
			height: 80vh;
			grid-column: wide;

			> * {
				grid-column: 1;
			}
		}

		.achord-sidebar {
			display: none;
		}

		.achord-content {
			grid-row: 2;
		}

		.achord-channel {
			grid-row: 1;
		}
	}
</style>
